import './App.scss';

import { useNavigate, useSearchParams } from 'react-router-dom';
import React, { Suspense, useCallback, useState } from 'react';
import { CssBaseline } from '@mui/material';
import '@fontsource/roboto';

import LoginMain from "./components/Login/LoginMain";
import ApplicationProviders from "helpers/context/Application/ApplicationProviders";
import NonAuthenticatingProviders from "helpers/context/NonAuthenticatingProviders";
import OfflineMessage from "components/Offline/OfflineMessage";

import APIHelper from 'helpers/APIHelper';
import WebResponse, { ResponseTypeEnum } from 'helpers/WebResponse';
import API from "helpers/API";
import useAuthHandler from "helpers/context/Authentication/useAuthHandler";
import useAuthInfo from "helpers/context/Authentication/useAuthInfo";
import LoadingLinearProgress from "components/Common/LoadingLinearProgress";
import AppAuthorized from "AppAuthorized";
import useErrorActions from "components/Errors/useErrorActions";
import useNavigationBlockerActions from "helpers/context/Application/useNavigationBlockerActions";
import { CaseInsensitiveCompare, isNullOrWhiteSpace } from "helpers/objects";
import useConfiguratorHandler from "helpers/context/Configurator/useConfiguratorHandler";
import Constants from "helpers/constants";

//FUTURE: Further investigate lazy loading of components to create smaller js bundle on initial load
// const AppAuthorized = React.lazy(() => import("./AppAuthorized"));



function App() {

  const navigate = useNavigate();
  const [query] = useSearchParams();

  const authHandler = useAuthHandler();
  const authInfo = useAuthInfo();
  const configuratorHandler = useConfiguratorHandler();

  const [isOffline, setIsOffline] = React.useState<boolean | null>(null);
  const errorActions = useErrorActions();
    const navBlockerActions = useNavigationBlockerActions();
    const [loadedFirstTime, setLoadedFirstTime] = useState<boolean>(false);

  const embed = query.get("embed") ?? query.get("Embed") ?? undefined;
  const configurator = query.get("configurator") ?? query.get("Configurator") ?? undefined;
  const providedToken = query.get("token") ?? query.get("Token") ?? undefined;

  const checkOffline = React.useCallback(async () => {
    for (let attempt = 0; attempt < 2; attempt++) {
      const r = await API.PingOnline();
      setIsOffline(!r.online);
      if (r.authenticated) {
        //If we are loading the page from scratch, but the server pings back that we actually are authenticated, then we set our internal state to be logged in so we don't 
        // force the user back to the login page
        const token = localStorage.getItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY);
        if (isNullOrWhiteSpace(localStorage.getItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY)) && token !== "undefined" && token !== "null") {
          authHandler.SetUserAsAuthenticated();
        } else {
          authHandler.SetUserAsBearerAuthenticated();
        }
        break;
      } else {
        // Try to refresh the token, if it exists, and try again
        if (attempt === 0 && await APIHelper.RefreshBearerToken()) {
          continue;
        } else {
          localStorage.removeItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY);
          break;
        }
      }
    }
    if (!providedToken || CaseInsensitiveCompare(providedToken, "null")) {
      setLoadedFirstTime(true);
    }

  }, [providedToken, authHandler]);

  React.useEffect(() => {
    if (embed && CaseInsensitiveCompare(embed, "true")) {
      configuratorHandler.SetInstanceIsEmbedded();
    }
    if (configurator) {
      configuratorHandler.SetConfiguratorMode(configurator);
    }
    if (providedToken) {
      if (CaseInsensitiveCompare(providedToken, "null")) {
        localStorage.removeItem(Constants.LocalStorageNames.LOCAL_STORAGE_TOKEN_KEY);
      } else {
        authHandler.SignInWithTokenAsync(providedToken).then(() => {
          setLoadedFirstTime(true);
        });
      }
    }
  }, [embed, configuratorHandler, configurator, providedToken, authHandler]);

  React.useEffect(() => {
    // Handle knowing if the site is offline on pages that do not make API calls, like login screen
    checkOffline();
  }, [checkOffline]);

  React.useEffect(() => {

    const preRequestCallback = () => {
      if (authInfo.signedIn && !authInfo.timedOut) {
        if (!authInfo.signedInWithBearerToken) {
          authHandler.ResetTimer();
        }
      }
    };

    const postRequestCallback = (wr: WebResponse<any>) => {

      if (authInfo.signedIn) {

        if (wr.GetResponseType() === ResponseTypeEnum.ServiceUnavailable) {
          // Catches if the site goes offline while user is signed in
          navBlockerActions.Unblock();
          setIsOffline(true);
          authHandler.SignOutAsync();
        } else if (wr.AuthRequired) {
          // if we received an unauthorized, then our credentials must be stale
          navBlockerActions.Unblock();
          authHandler.SignOutAsync().then(() => {
            navigate("/login");
          });
        }
      }
    };

    const handleErrorCallback = (errorMessage: string, redirectToErrorPage: boolean) => {
      if (redirectToErrorPage) {
        navBlockerActions.Unblock();
        errorActions.NavigateToErrorPage(errorMessage)
      }
      else {
        console.log(errorMessage);
      }
    };

    APIHelper.registerLayoutCallback(preRequestCallback, postRequestCallback, handleErrorCallback);

  }, [navigate, authHandler, authInfo, errorActions, navBlockerActions]);

  return <NonAuthenticatingProviders>

    <CssBaseline />

    {isOffline && <>
      <OfflineMessage signedIn={authInfo.signedIn} checkOffline={checkOffline} />
    </>}

    {!authInfo.signedIn && isOffline === false && <>
      <LoginMain />
    </>}

    {authInfo.signedIn && isOffline === false && <>
      <ApplicationProviders>
        <Suspense fallback={<LoadingLinearProgress />} >
          <AppAuthorized />
        </Suspense>
      </ApplicationProviders>
    </>}
  </NonAuthenticatingProviders>;

}
export default App;


